/**
 * =============================================================================
 * IDS CRM Web - Asset Detail Page
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BackToList, { useBackToList } from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_ASSET_CONFIG, getAssetTypeMeta, getOptionLabel, normaliseAssetConfig, resolveAssetImageSrc } from '../utils/assetTypes';
import { authedFileHref } from '../utils/files';
import CheckFormReview from '../components/CheckFormReview';
import { daysFromToday, formatDateKey } from '../utils/dates';
import { formatDate as formatInstant } from '../utils/format';
import { useConfirm } from '../components/ConfirmDialog';
import AssetComponentsPanel from '../components/AssetComponentsPanel';
import './AssetDetail.css';

const FIELD_LABELS = {
  customer_id: 'Customer',
  farm_site_id: 'Farm Site',
  serial_number: 'Serial Number',
  quote_number: 'Quote Number',
  robot_type: 'Asset Type',
  build_date: 'Build Date',
  install_date: 'Install Date',
  warranty_expiry_date: 'Warranty Expiry',
  brush_life_cleans: 'Brush Life (cleans)',
  brush_estimated_life_days: 'Brush Est. Life (days)',
  spray_chemical: 'Spray Chemical',
  spray_dilution_ratio: 'Spray Dilution Ratio',
  install_notes: 'Install Notes',
  remote_access_id: 'Remote Access ID',
  general_project_description: 'Project Description',
  invoicing_type: 'Invoicing Type',
  invoice_amount_minor_units: 'Invoice Amount',
  status: 'Status',
  health_flag: 'Health Flag',
  decommission_date: 'Decommission Date',
  decommission_reason: 'Decommission Reason',
  replaced_by_asset_id: 'Replaced By',
  control_cabinet_serial: 'Control Cabinet Serial',
  power_cabinet_serial: 'Power Cabinet Serial',
  annual_service_duration_hours: 'Annual Service Duration',
  health_check_duration_hours: 'Quarterly Inspection Duration',
  robot_serial_1: 'Robot 1 Serial',
  robot_serial_2: 'Robot 2 Serial',
  krc_cabinet_serial_1: 'KRC Cabinet 1 Serial',
  krc_cabinet_serial_2: 'KRC Cabinet 2 Serial',
  kss_version_1: 'KSS Version 1',
  kss_version_2: 'KSS Version 2',
  plc_version: 'PLC Version',
  waybill_number: 'Waybill Number',
  deviation_notes: 'Deviation Notes'
};

function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // After a delete, back to the Assets list as it was left.
  const backToList = useBackToList('/assets');
  const { confirmElement, requestConfirm } = useConfirm();
  const { authFetch, user } = useAuth();

  const [asset, setAsset] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [workHistory, setWorkHistory] = useState([]);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [maintenanceTotal, setMaintenanceTotal] = useState(0);
  const [nextMaintenance, setNextMaintenance] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [robotChecks, setRobotChecks] = useState([]);
  const [genericTemplateChecks, setGenericTemplateChecks] = useState([]);
  const [genericTemplateChecksError, setGenericTemplateChecksError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [assetConfig, setAssetConfig] = useState(DEFAULT_ASSET_CONFIG);
  const [selectedMaintenanceTask, setSelectedMaintenanceTask] = useState(null);
  const [selectedRobotCheck, setSelectedRobotCheck] = useState(null);
  const [selectedGenericCheck, setSelectedGenericCheck] = useState(null);
  const [showCadenceModal, setShowCadenceModal] = useState(false);
  const [cadenceSaving, setCadenceSaving] = useState(false);
  const [cadenceError, setCadenceError] = useState(null);
  const [cadenceForm, setCadenceForm] = useState({
    service_name: '',
    service_type: 'health_check',
    cadence_interval: 3,
    cadence_unit: 'months',
    first_due_date: '',
    end_date: '',
    occurrences: '',
    notes: ''
  });
  // Service cover (per-asset commercial setting, 2026-07-23)
  const [editingCover, setEditingCover] = useState(false);
  const [savingCover, setSavingCover] = useState(false);
  const [coverError, setCoverError] = useState(null);
  const [coverForm, setCoverForm] = useState({ designation: '', end_date: '' });

  const fetchAsset = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/assets/${id}`);
      if (!response.ok) throw new Error('Asset not found');
      setAsset(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, authFetch]);

  const saveCover = async () => {
    setSavingCover(true);
    setCoverError(null);
    try {
      const body = coverForm.designation
        ? { cover_policy: { designation: coverForm.designation }, cover_policy_end_date: coverForm.end_date }
        : { cover_policy: null, cover_policy_end_date: null };
      const response = await authFetch(`/api/v1/assets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save service cover');
      }
      setEditingCover(false);
      fetchAsset();
    } catch (err) {
      setCoverError(err.message);
    } finally {
      setSavingCover(false);
    }
  };

  const fetchTickets = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/assets/${id}/tickets`);
      const data = await response.json();
      setTickets(data.data || []);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
  }, [id, authFetch]);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/assets/${id}/tasks`);
      const data = await response.json();
      setTasks(data.data || []);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  }, [id, authFetch]);

  const fetchWorkHistory = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/assets/${id}/work-history`);
      const data = await response.json();
      setWorkHistory(data.data || []);
    } catch (err) {
      console.error('Failed to fetch asset work history:', err);
    }
  }, [id, authFetch]);

  const fetchMaintenanceTasks = useCallback(async () => {
    try {
      // A robot asset carries up to 40 tasks (20 scheduled + history); the
      // API default of 25 silently hid the rest. limit=100 covers robot +
      // cadence schedules; the total is surfaced so truncation is visible.
      const response = await authFetch(`/api/v1/maintenance/tasks?asset_id=${id}&limit=100`);
      const data = await response.json();
      setMaintenanceTasks(data.data || []);
      setMaintenanceTotal(data.pagination?.total ?? (data.data || []).length);
    } catch (err) {
      console.error('Failed to fetch maintenance tasks:', err);
    }
  }, [id, authFetch]);

  const fetchNextMaintenance = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/maintenance/tasks?asset_id=${id}&status=scheduled&limit=1`);
      const data = await response.json();
      if (data.data && data.data.length > 0) {
        setNextMaintenance(data.data[0]);
      }
    } catch (err) {
      console.error('Failed to fetch next maintenance:', err);
    }
  }, [id, authFetch]);

  const fetchAuditLog = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/assets/${id}/audit-log`);
      const data = await response.json();
      setAuditLog(data.data || []);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
    }
  }, [id, authFetch]);

  const fetchRobotChecks = useCallback(async () => {
    try {
      const response = await authFetch(`/api/v1/assets/${id}/robot-checks`);
      const data = await response.json();
      setRobotChecks(data.data || []);
    } catch (err) {
      console.error('Failed to fetch robot checks:', err);
    }
  }, [id, authFetch]);

  const fetchGenericTemplateChecks = useCallback(async () => {
    try {
      setGenericTemplateChecksError(null);
      const response = await authFetch(`/api/v1/assets/${id}/template-checks`);
      if (!response.ok) {
        throw new Error('Failed to load generic template checks');
      }
      const data = await response.json();
      setGenericTemplateChecks(data.data || []);
    } catch (err) {
      console.error('Failed to fetch generic template checks:', err);
      setGenericTemplateChecksError(err.message || 'Failed to load generic checks');
    }
  }, [id, authFetch]);

  useEffect(() => {
    fetchAsset();
    fetchNextMaintenance();
    fetchWorkHistory();
  }, [fetchAsset, fetchNextMaintenance, fetchWorkHistory]);

  useEffect(() => {
    const fetchAssetConfig = async () => {
      try {
        const response = await authFetch('/api/v1/assets/config');
        if (!response.ok) return;
        const data = await response.json();
        setAssetConfig(normaliseAssetConfig(data.data));
      } catch (err) {
        console.error('Failed to fetch asset config:', err);
      }
    };

    fetchAssetConfig();
  }, [authFetch]);

  useEffect(() => {
    if (activeTab === 'tickets') fetchTickets();
    if (activeTab === 'tasks') fetchTasks();
    if (activeTab === 'maintenance') fetchMaintenanceTasks();
    if (activeTab === 'audit') fetchAuditLog();
    if (activeTab === 'robot-checks') {
      fetchRobotChecks();
      fetchGenericTemplateChecks();
    }
  }, [activeTab, fetchTickets, fetchTasks, fetchMaintenanceTasks, fetchAuditLog, fetchRobotChecks, fetchGenericTemplateChecks]);

  // DATE columns (install, warranty, cover, due and scheduled dates) are
  // calendar days. Timestamps (opened_at, resolved_at, completed_at) are
  // instants and go through formatInstantDate.
  const formatDate = (date) => formatDateKey(date, { day: 'numeric', month: 'short', year: 'numeric' }, 'Not recorded');
  const formatInstantDate = (value) => (value ? formatInstant(value) : 'Not recorded');

  const displayValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not recorded';
    return String(value).replace(/_/g, ' ');
  };

  const getWorkTypeLabel = (type) => ({
    reactive_ticket: 'Reactive Ticket',
    scheduled_work: 'Scheduled Work'
  }[type] || displayValue(type));

  const displayNumber = (value) => {
    if (value === null || value === undefined || value === '') return 'Not recorded';
    return Number(value).toLocaleString();
  };

  // Warranty and cover run to the end of their last day.
  const isOverdue = (date) => {
    const days = daysFromToday(date);
    return days !== null && days < 0;
  };

  const getDaysUntil = (date) => daysFromToday(date);

  const getDueStatus = (date, warningDays = 30) => {
    const days = getDaysUntil(date);
    if (days === null) return { label: 'Not scheduled', className: 'neutral' };
    if (days < 0) return { label: `${Math.abs(days)}d overdue`, className: 'danger' };
    if (days <= warningDays) return { label: `${days}d due`, className: 'warning' };
    return { label: `${days}d`, className: 'healthy' };
  };

  const getSiteAddress = () => [
    asset?.site_address_line_1,
    asset?.site_address_line_2,
    asset?.site_town_city,
    asset?.site_county_region,
    asset?.site_state_province,
    asset?.site_postcode
  ].filter(Boolean).join(', ');

  const handleDeleteAsset = () => {
    const label = asset?.asset_ref || asset?.serial_number || 'this asset';
    requestConfirm({
      title: 'Delete asset?',
      message: `Delete ${label}? This will remove it from active asset lists, reports, maps, and customer records.`,
      confirmLabel: 'Delete Asset',
      danger: true,
      onConfirm: performDeleteAsset
    });
  };

  const performDeleteAsset = async () => {
    setDeleting(true);
    setError(null);

    try {
      const response = await authFetch(`/api/v1/assets/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete asset');
      }

      backToList();
    } catch (err) {
      setError(err.message || 'Failed to delete asset');
      setDeleting(false);
    }
  };

  if (loading) return <div className="loading-page">Loading asset...</div>;

  if (error) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <BackToList to="/assets" className="btn-secondary">← Assets</BackToList>
      </div>
    );
  }

  const warrantyStatus = getDueStatus(asset.warranty_expiry_date, 60);
  const maintenanceStatus = nextMaintenance
    ? getDueStatus(nextMaintenance.current_due_date, 30)
    : { label: 'Not scheduled', className: 'neutral' };
  const assetType = getAssetTypeMeta(asset.robot_type, assetConfig);
  const statusLabel = getOptionLabel(assetConfig.statuses, asset.status);
  const healthLabel = getOptionLabel(assetConfig.health_flags, asset.health_flag);
  const isGeneralProject = asset.robot_type === 'general_project';
  const isDualType = asset.robot_type.includes('dual');
  const isBrushType = asset.robot_type.includes('brush');
  const showAlerts = nextMaintenance && nextMaintenance.is_overdue;
  const getMaintenanceTaskName = (task) => {
    if (!task) return 'None scheduled';
    if (!task.is_robot_schedule && task.task_name) return task.task_name;
    return task.task_type === 'annual_service'
      ? 'Annual Service'
      : `Quarterly Inspection ${task.health_check_number}`;
  };
  const ROBOT_ASSET_TYPES = ['pre_brush', 'post_spray', 'pre_spray', 'dual_pre_brush', 'dual_post_spray', 'dual_pre_spray'];
  const isRobotAsset = ROBOT_ASSET_TYPES.includes(asset.robot_type);
  const canConfigureCadence = !isRobotAsset && !isGeneralProject && ['admin', 'manager'].includes(user?.role);

  return (
    <div className="asset-detail">
      {confirmElement}
      <div className="detail-header">
        <div className="header-left">
          <div className="asset-avatar image-avatar">
            <img src={resolveAssetImageSrc(assetType.image)} alt={assetType.label} />
          </div>
          <div className="header-info">
            <BackToList to="/assets" className="back-link">← Assets</BackToList>
            <div className="header-row-1">
              <h1>{asset.asset_ref}</h1>
              <span className="asset-ref">{asset.serial_number}</span>
              <span className={`type-chip ${asset.robot_type}`}>{assetType.label}</span>
              <span className={`status-chip ${asset.status}`}>{statusLabel}</span>
              {asset.health_flag && <span className={`health-chip ${asset.health_flag}`}>{healthLabel}</span>}
            </div>
            <div className="header-meta">
              <span className="meta-item">
                {asset.customer_name ? <Link to={`/customers/${asset.customer_id}`}>{asset.customer_name}</Link> : 'Customer not recorded'}
              </span>
              <span className="meta-item">
                {asset.site_name ? <Link to={`/customers/${asset.customer_id}?tab=sites`}>{asset.site_name}</Link> : 'Site not recorded'}
              </span>
              <span className="meta-item">Installed {formatDate(asset.install_date)}</span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <Link to={`/hierarchy/asset/${id}`} className="btn-secondary">Hierarchy</Link>
          <Link to={`/assets/${id}/edit`} className="btn-secondary">Edit Asset</Link>
          {['manager', 'admin'].includes(user?.role) && (
            <button type="button" className="btn-danger" onClick={handleDeleteAsset} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Asset'}
            </button>
          )}
        </div>
      </div>

      {asset.status === 'pending_install' && (
        <div className="pending-install-banner">
          <span>
            This asset was generated from a won sales line and is awaiting install.
            {(() => {
              const missing = [
                !asset.serial_number && 'serial number',
                !asset.farm_site_id && 'farm site',
                !asset.install_date && 'install date'
              ].filter(Boolean);
              return missing.length > 0
                ? ` Complete the record before the install-complete stage — missing: ${missing.join(', ')}.`
                : ' The record is complete — it activates when the line item reaches its install-complete stage.';
            })()}
          </span>
          <Link to={`/assets/${id}/edit`} className="btn-primary">Complete Record</Link>
        </div>
      )}

      <div className="asset-stats">
        <div className="stat-card">
          <div className="stat-label">STATUS</div>
          <div className="stat-value text">{statusLabel}</div>
          <div className="stat-sub">{asset.health_flag ? `${healthLabel} health` : 'No health flag'}</div>
        </div>
        {!isGeneralProject && (
          <div className="stat-card">
            <div className="stat-label">NEXT<br/>MAINTENANCE</div>
            <div className={`stat-value ${maintenanceStatus.className}`}>{maintenanceStatus.label}</div>
            <div className="stat-sub">{nextMaintenance ? getMaintenanceTaskName(nextMaintenance) : 'No schedule'}</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-label">WARRANTY</div>
          <div className={`stat-value ${warrantyStatus.className}`}>{warrantyStatus.label}</div>
          <div className="stat-sub">{formatDate(asset.warranty_expiry_date)}</div>
        </div>
        {isBrushType && (
          <div className="stat-card">
            <div className="stat-label">BRUSH LIFE</div>
            <div className="stat-value text">{displayNumber(asset.brush_life_cleans)}</div>
            <div className="stat-sub">cleans</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-label">REMOTE</div>
          <div className="stat-value text">{displayValue(asset.remote_access_id)}</div>
          <div className="stat-sub">{asset.remote_access_id ? 'Configured' : 'Not configured'}</div>
        </div>
      </div>

      {showAlerts && (
        <div className="alerts-banner">
          <div className="alert-item service">
            {getMaintenanceTaskName(nextMaintenance)} overdue by {nextMaintenance.calculated_days_overdue || nextMaintenance.days_overdue} days (due {formatDate(nextMaintenance.current_due_date)})
          </div>
        </div>
      )}

      <div className="detail-tabs">
        <button className={activeTab === 'details' ? 'tab active' : 'tab'} onClick={() => setActiveTab('details')}>Details</button>
        {!isGeneralProject && (
          <button className={activeTab === 'maintenance' ? 'tab active' : 'tab'} onClick={() => setActiveTab('maintenance')}>
            Maintenance ({maintenanceTotal || maintenanceTasks.length})
          </button>
        )}
        <button className={activeTab === 'components' ? 'tab active' : 'tab'} onClick={() => setActiveTab('components')}>Components</button>
        <button className={activeTab === 'history' ? 'tab active' : 'tab'} onClick={() => setActiveTab('history')}>History ({workHistory.length})</button>
        <button className={activeTab === 'robot-checks' ? 'tab active' : 'tab'} onClick={() => setActiveTab('robot-checks')}>Asset Checks ({robotChecks.length + genericTemplateChecks.length})</button>
        <button className={activeTab === 'tickets' ? 'tab active' : 'tab'} onClick={() => setActiveTab('tickets')}>Tickets ({tickets.length})</button>
        <button className={activeTab === 'tasks' ? 'tab active' : 'tab'} onClick={() => setActiveTab('tasks')}>Scheduled Tasks ({tasks.length})</button>
        <button className={activeTab === 'audit' ? 'tab active' : 'tab'} onClick={() => setActiveTab('audit')}>Audit Log ({auditLog.length})</button>
      </div>

      <div className="detail-content">
        {activeTab === 'details' && (
          <div className="details-grid">
            <div className="detail-card">
              <div className="card-header">
                <h3>ASSET</h3>
              </div>
              <div className="card-body">
                <div className="asset-type-preview">
                  <img src={resolveAssetImageSrc(assetType.image)} alt={assetType.label} />
                  <div>
                    <span>{assetType.label}</span>
                    <small>{assetType.summary}</small>
                  </div>
                </div>
                <div className="info-row"><span className="info-label">Serial</span><span className="info-value mono">{asset.serial_number}</span></div>
                {asset.quote_number && <div className="info-row"><span className="info-label">Q-Number</span><span className="info-value mono">{asset.quote_number}</span></div>}
                <div className="info-row"><span className="info-label">Build date</span><span className="info-value">{formatDate(asset.build_date)}</span></div>
              </div>
            </div>

            {isGeneralProject && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <h3>GENERAL PROJECT</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">Description</span><span className="info-value">{displayValue(asset.general_project_description)}</span></div>
                  <div className="info-row"><span className="info-label">Invoicing</span><span className="info-value">{displayValue(asset.invoicing_type)}</span></div>
                  {asset.invoicing_type === 'single' ? (
                    <div className="info-row"><span className="info-label">Amount</span><span className="info-value">{asset.invoice_amount_minor_units ? `£${(Number(asset.invoice_amount_minor_units) / 100).toFixed(2)}` : 'Not recorded'}</span></div>
                  ) : (
                    <div className="info-row">
                      <span className="info-label">Milestones</span>
                      <span className="info-value">
                        {Array.isArray(asset.invoice_milestones) && asset.invoice_milestones.length > 0
                          ? asset.invoice_milestones.map(milestone => `${milestone.name}: £${(Number(milestone.amount_minor_units || 0) / 100).toFixed(2)}`).join(', ')
                          : 'Not recorded'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="detail-card">
              <div className="card-header">
                <h3>CUSTOMER & SITE</h3>
              </div>
              <div className="card-body">
                <div className="info-row">
                  <span className="info-label">Customer</span>
                  <Link to={`/customers/${asset.customer_id}`} className="link-value">{asset.customer_name}</Link>
                </div>
                <div className="info-row">
                  <span className="info-label">Farm site</span>
                  <Link to={`/customers/${asset.customer_id}?tab=sites`} className="link-value">{displayValue(asset.site_name)}</Link>
                </div>
                <div className="info-row"><span className="info-label">Address</span><span className="info-value">{getSiteAddress() || 'Not recorded'}</span></div>
                <div className="info-row"><span className="info-label">Territory</span><span className="info-value">{displayValue(asset.site_territory)}</span></div>
              </div>
            </div>

            <div className="detail-card">
              <div className="card-header">
                <h3>INSTALLATION</h3>
              </div>
              <div className="card-body">
                <div className="info-row"><span className="info-label">Installed</span><span className="info-value">{formatDate(asset.install_date)}</span></div>
                <div className="info-row"><span className="info-label">Warranty</span><span className={`info-value ${isOverdue(asset.warranty_expiry_date) ? '' : 'highlight'}`}>{formatDate(asset.warranty_expiry_date)}</span></div>
                <div className="info-row"><span className="info-label">Service Interval</span><span className="info-value">{asset.service_interval_months ? `${asset.service_interval_months} months` : 'Not recorded'}</span></div>
                {asset.linked_opportunity_id && (
                  <div className="info-row">
                    <span className="info-label">Opportunity</span>
                    <Link to={`/pipeline/${asset.linked_opportunity_id}`} className="link-value">{asset.opp_ref}</Link>
                  </div>
                )}
              </div>
            </div>

            {/* Service cover: a per-asset commercial setting (2026-07-23) —
                warranty is a product fact; cover is this machine's contract. */}
            <div className="detail-card">
              <div className="card-header">
                <h3>SERVICE COVER</h3>
              </div>
              <div className="card-body">
                {!editingCover ? (
                  <>
                    <div className="info-row">
                      <span className="info-label">Designation</span>
                      <span className={`info-value ${asset.cover_policy ? 'highlight' : ''}`}>
                        {asset.cover_policy
                          ? (asset.cover_policy.designation === 'service' ? 'Service'
                            : asset.cover_policy.designation === 'repair_replace' ? 'Repair & Replace'
                            : 'Service / Repair & Replace')
                          : 'None — billable terms'}
                      </span>
                    </div>
                    {asset.cover_policy && (
                      <>
                        <div className="info-row">
                          <span className="info-label">Cover until</span>
                          <span className={`info-value ${isOverdue(asset.cover_policy_end_date) ? '' : 'highlight'}`}>
                            {formatDate(asset.cover_policy_end_date)}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Parts</span>
                          <span className="info-value">
                            {asset.cover_policy.scope?.parts === false ? 'Chargeable' : 'Covered'}
                          </span>
                        </div>
                      </>
                    )}
                    {(user?.role === 'admin' || user?.role === 'manager') && (
                      <button
                        type="button"
                        className="btn-small btn-secondary"
                        onClick={() => {
                          setCoverForm({
                            designation: asset.cover_policy?.designation
                              || (asset.cover_policy ? 'repair_replace' : ''),
                            end_date: asset.cover_policy_end_date ? String(asset.cover_policy_end_date).slice(0, 10) : ''
                          });
                          setEditingCover(true);
                        }}
                      >
                        {asset.cover_policy ? 'Change cover' : 'Set cover'}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="info-row">
                      <span className="info-label">Designation</span>
                      <select
                        value={coverForm.designation}
                        onChange={(e) => setCoverForm(prev => ({ ...prev, designation: e.target.value }))}
                      >
                        <option value="">None — billable terms</option>
                        <option value="service">Service (parts chargeable)</option>
                        <option value="repair_replace">Repair & Replace (parts covered)</option>
                      </select>
                    </div>
                    {coverForm.designation && (
                      <div className="info-row">
                        <span className="info-label">Cover until</span>
                        <input
                          type="date"
                          value={coverForm.end_date}
                          onChange={(e) => setCoverForm(prev => ({ ...prev, end_date: e.target.value }))}
                        />
                      </div>
                    )}
                    {coverError && <div className="admin-message error">{coverError}</div>}
                    <div className="info-row">
                      <button type="button" className="btn-small btn-secondary" onClick={() => { setEditingCover(false); setCoverError(null); }}>Cancel</button>
                      <button
                        type="button"
                        className="btn-small btn-primary"
                        disabled={savingCover || (Boolean(coverForm.designation) && !coverForm.end_date)}
                        onClick={saveCover}
                      >
                        {savingCover ? 'Saving...' : 'Save cover'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            
            <div className="detail-card">
              <div className="card-header">
                <h3>SITE PARLOUR</h3>
              </div>
              <div className="card-body">
                <div className="info-row"><span className="info-label">Type</span><span className="info-value">{displayValue(asset.site_parlour_type)}</span></div>
                <div className="info-row">
                  <span className="info-label">Manufacturer</span>
                  <span className="info-value">
                    {asset.site_parlour_manufacturer === 'other'
                      ? displayValue(asset.site_parlour_manufacturer_other)
                      : displayValue(asset.site_parlour_manufacturer)}
                  </span>
                </div>
                <div className="info-row"><span className="info-label">Size</span><span className="info-value">{asset.site_parlour_size ? `${asset.site_parlour_size} points` : 'Not recorded'}</span></div>
                <div className="info-row"><span className="info-label">Rotation</span><span className="info-value">{displayValue(asset.site_rotation_direction)}</span></div>
              </div>
            </div>

            <div className="detail-card">
              <div className="card-header">
                <h3>SITE HERD</h3>
              </div>
              <div className="card-body">
                <div className="info-row"><span className="info-label">Total herd size</span><span className="info-value">{displayNumber(asset.site_cows_onsite)}</span></div>
                <div className="info-row"><span className="info-label">Cows milking</span><span className="info-value highlight">{displayNumber(asset.site_cows_milking)}</span></div>
                <div className="info-row"><span className="info-label">Breed</span><span className="info-value">{displayValue(asset.site_breed)}</span></div>
                <div className="info-row"><span className="info-label">Milk buyer</span><span className="info-value">{displayValue(asset.site_milk_buyer)}</span></div>
              </div>
            </div>

            {!isGeneralProject && (
              <>
                <div className="detail-card">
                  <div className="card-header">
                    <h3>CHEMICAL CONFIGURATION</h3>
                  </div>
                  <div className="card-body">
                    <div className="info-row"><span className="info-label">Chemical</span><span className="info-value">{displayValue(asset.spray_chemical)}</span></div>
                    <div className="info-row"><span className="info-label">Dilution</span><span className="info-value">{displayValue(asset.spray_dilution_ratio)}</span></div>
                  </div>
                </div>

                <div className="detail-card">
                  <div className="card-header">
                    <h3>CONNECTIVITY</h3>
                  </div>
                  <div className="card-body">
                    <div className="info-row"><span className="info-label">Remote Access</span><span className="info-value">{displayValue(asset.remote_access_id)}</span></div>
                  </div>
                </div>
              </>
            )}

            {(asset.control_cabinet_serial || asset.power_cabinet_serial || asset.pump_cabinet_serial_1) && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>CABINET SERIALS</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">Control Cabinet</span><span className="info-value mono">{displayValue(asset.control_cabinet_serial)}</span></div>
                  <div className="info-row"><span className="info-label">Power Cabinet</span><span className="info-value mono">{displayValue(asset.power_cabinet_serial)}</span></div>
                  <div className="info-row"><span className="info-label">Pump Cabinet</span><span className="info-value mono">{displayValue(asset.pump_cabinet_serial_1)}</span></div>
                  {isDualType && asset.pump_cabinet_serial_2 && (
                    <div className="info-row"><span className="info-label">Pump Cabinet 2</span><span className="info-value mono">{displayValue(asset.pump_cabinet_serial_2)}</span></div>
                  )}
                </div>
              </div>
            )}

            {(asset.robot_serial_1 || asset.krc_cabinet_serial_1) && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>KUKA ROBOT {isDualType ? '1' : ''}</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">Robot Ser No.</span><span className="info-value mono">{displayValue(asset.robot_serial_1)}</span></div>
                  <div className="info-row"><span className="info-label">KRC Cabinet</span><span className="info-value mono">{displayValue(asset.krc_cabinet_serial_1)}</span></div>
                  {/* KPP/KSP/PC/wrist/resolver/SmartPAD live in the component
                      registry now — one home per part, with movement history. */}
                  <div className="info-row">
                    <span className="info-label">Fitted parts</span>
                    <span className="info-value">
                      <button type="button" className="link-button" onClick={() => setActiveTab('components')}>See Components tab</button>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {isDualType && (asset.robot_serial_2 || asset.krc_cabinet_serial_2) && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>KUKA ROBOT 2</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">Robot Ser No.</span><span className="info-value mono">{displayValue(asset.robot_serial_2)}</span></div>
                  <div className="info-row"><span className="info-label">KRC Cabinet</span><span className="info-value mono">{displayValue(asset.krc_cabinet_serial_2)}</span></div>
                  <div className="info-row">
                    <span className="info-label">Fitted parts</span>
                    <span className="info-value">
                      <button type="button" className="link-button" onClick={() => setActiveTab('components')}>See Components tab</button>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {(asset.kss_version_1 || asset.plc_version || asset.tool_camera_version) && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>SOFTWARE VERSIONS</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">{isDualType ? 'Robot 1 KSS' : 'KSS Version'}</span><span className="info-value">{displayValue(asset.kss_version_1)}</span></div>
                  {isDualType && <div className="info-row"><span className="info-label">Robot 2 KSS</span><span className="info-value">{displayValue(asset.kss_version_2)}</span></div>}
                  <div className="info-row"><span className="info-label">PLC SW Ver</span><span className="info-value">{displayValue(asset.plc_version)}</span></div>
                  <div className="info-row"><span className="info-label">Tool Camera</span><span className="info-value">{displayValue(asset.tool_camera_version)}</span></div>
                </div>
              </div>
            )}

            {(asset.cow_locate_camera_enabled || asset.cow_locate_mac_id) && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>COW LOCATE CAMERA</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">Installed</span><span className="info-value">{asset.cow_locate_camera_enabled ? 'Yes' : 'No'}</span></div>
                  <div className="info-row"><span className="info-label">MAC ID</span><span className="info-value mono">{displayValue(asset.cow_locate_mac_id)}</span></div>
                </div>
              </div>
            )}

            {(asset.beckoff_plc_serial || asset.input_module_serial_1 || asset.safety_module_serial) && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>BECKOFF PARTS</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">PLC Serial</span><span className="info-value mono">{displayValue(asset.beckoff_plc_serial)}</span></div>
                  <div className="info-row"><span className="info-label">DI 1809</span><span className="info-value mono">{displayValue(asset.input_module_serial_1)}</span></div>
                  {isDualType && <div className="info-row"><span className="info-label">DI 1809 2</span><span className="info-value mono">{displayValue(asset.input_module_serial_2)}</span></div>}
                  <div className="info-row"><span className="info-label">Safety Module</span><span className="info-value mono">{displayValue(asset.safety_module_serial)}</span></div>
                  <div className="info-row"><span className="info-label">Output Module</span><span className="info-value mono">{displayValue(asset.output_module_serial)}</span></div>
                  <div className="info-row"><span className="info-label">EJ Coupler</span><span className="info-value mono">{displayValue(asset.ej_coupler_serial)}</span></div>
                  <div className="info-row"><span className="info-label">PTL PCB</span><span className="info-value mono">{displayValue(asset.ptl_pcb_serial)}</span></div>
                  <div className="info-row"><span className="info-label">PSU 2420</span><span className="info-value mono">{displayValue(asset.beckoff_psu_2420_serial)}</span></div>
                  <div className="info-row"><span className="info-label">PSU 4810</span><span className="info-value mono">{displayValue(asset.beckoff_psu_4810_serial)}</span></div>
                  <div className="info-row"><span className="info-label">Windows Version</span><span className="info-value">{displayValue(asset.beckoff_os_type)}</span></div>
                </div>
              </div>
            )}

            {(asset.pcb_type || asset.pin_type || asset.air_motor_type) && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>HARDWARE CONFIG</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">PCB Type</span><span className="info-value">{displayValue(asset.pcb_type)}</span></div>
                  <div className="info-row"><span className="info-label">Pin Type</span><span className="info-value">{displayValue(asset.pin_type)}</span></div>
                  <div className="info-row"><span className="info-label">Air Motor</span><span className="info-value">{displayValue(asset.air_motor_type)}</span></div>
                  <div className="info-row"><span className="info-label">{isDualType ? 'Robot 1 Air Motor' : 'Air Motor Serial'}</span><span className="info-value mono">{displayValue(asset.air_motor_serial_1)}</span></div>
                  {isDualType && <div className="info-row"><span className="info-label">Robot 2 Air Motor</span><span className="info-value mono">{displayValue(asset.air_motor_serial_2)}</span></div>}
                  <div className="info-row"><span className="info-label">Air Regulator</span><span className="info-value">{displayValue(asset.air_regulator_revision)}</span></div>
                  <div className="info-row"><span className="info-label">Pressure Relief</span><span className="info-value">{asset.pressure_relief_installed ? 'Installed' : 'Not installed'}</span></div>
                </div>
              </div>
            )}

            {(asset.unifi_router_size || asset.unifi_router_mac || asset.tsw_switch_serial) && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>NETWORK HARDWARE</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">UniFi Router</span><span className="info-value">{displayValue(asset.unifi_router_size)}</span></div>
                  <div className="info-row"><span className="info-label">UniFi MAC</span><span className="info-value mono">{displayValue(asset.unifi_router_mac)}</span></div>
                  <div className="info-row"><span className="info-label">TSW Switch</span><span className="info-value mono">{displayValue(asset.tsw_switch_serial)}</span></div>
                </div>
              </div>
            )}

            {asset.flojet_pump_serial && (
              <div className="detail-card">
                <div className="card-header">
                  <h3>PUMP CABINET</h3>
                </div>
                <div className="card-body">
                  <div className="info-row"><span className="info-label">FloJet Pump</span><span className="info-value mono">{displayValue(asset.flojet_pump_serial)}</span></div>
                </div>
              </div>
            )}

            {(asset.waybill_number || asset.deviation_notes) && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <h3>TRACKING</h3>
                </div>
                <div className="card-body">
                  {asset.waybill_number && <div className="info-row"><span className="info-label">Waybill Number</span><span className="info-value">{asset.waybill_number}</span></div>}
                  {asset.deviation_notes && <div className="info-row"><span className="info-label">Deviations</span><span className="info-value">{asset.deviation_notes}</span></div>}
                </div>
              </div>
            )}

            {asset.install_notes && (
              <div className="detail-card full-width">
                <div className="card-header">
                  <h3>INSTALL NOTES</h3>
                </div>
                <div className="card-body">
                  <p className="notes-content">{asset.install_notes}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'components' && (
          <AssetComponentsPanel assetId={id} />
        )}

        {activeTab === 'maintenance' && (
          <div className="maintenance-schedule">
            <div className="list-header">
              <h3>Maintenance Schedule</h3>
              {asset.contract_start_date && (
                <span className="contract-dates">
                  Contract: {formatDate(asset.contract_start_date)} - {formatDate(asset.contract_end_date)}
                </span>
              )}
              {canConfigureCadence && (
                <button className="btn-secondary" onClick={() => setShowCadenceModal(true)}>
                  Configure Service Schedule
                </button>
              )}
              {maintenanceTotal > maintenanceTasks.length && (
                <span className="truncation-note">
                  Showing {maintenanceTasks.length} of {maintenanceTotal} tasks
                </span>
              )}
            </div>
            {maintenanceTasks.length === 0 ? (
              <div className="empty-message">
                {asset.maintenance_schedule_generated
                  ? 'No maintenance tasks found'
                  : asset.install_date
                    ? 'Maintenance schedule not yet generated'
                    : 'Set an install date to generate maintenance schedule'}
              </div>
            ) : (
              <div className="records-table">
                <table>
                  <thead>
                    <tr>
                      <th>Ref</th>
                      <th>Task</th>
                      <th>Year</th>
                      <th>Original Due</th>
                      <th>Current Due</th>
                      <th>Status</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maintenanceTasks.map((task) => {
                      const taskName = getMaintenanceTaskName(task);
                      const taskIsOverdue = task.status === 'scheduled' && task.is_overdue;
                      return (
                        <tr
                          key={task.task_id}
                          className={`${taskIsOverdue ? 'overdue-row' : ''} clickable-row`}
                          onClick={() => navigate(`/maintenance/${task.task_id}`)}
                          title="Click to view task details"
                        >
                          <td className="record-ref">
                            <Link to={`/maintenance/${task.task_id}`} onClick={e => e.stopPropagation()}>
                              {task.task_ref}
                            </Link>
                          </td>
                          <td>{taskName}</td>
                          <td>{task.cycle_year ? `Year ${task.cycle_year}` : '-'}</td>
                          <td>{formatDate(task.original_scheduled_date)}</td>
                          <td className={task.original_scheduled_date !== task.current_due_date ? 'rescheduled' : ''}>
                            {formatDate(task.current_due_date)}
                            {task.original_scheduled_date !== task.current_due_date && <span className="reschedule-badge">Rescheduled</span>}
                          </td>
                          <td>
                            <span className={`status-chip ${task.status}${taskIsOverdue ? ' overdue' : ''}`}>
                              {taskIsOverdue ? `Overdue (${task.calculated_days_overdue}d)` : displayValue(task.status)}
                            </span>
                          </td>
                          <td>
                            {task.completed_date ? (
                              <>
                                {formatDate(task.completed_date)}
                                {task.days_overdue > 0 && <span className="overdue-badge">{task.days_overdue}d late</span>}
                              </>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <RecordTable
            title="Jobs & Tickets History"
            emptyText="No ticket or scheduled work history for this asset"
            columns={['Ref', 'Date Raised', 'Work Type', 'Fault / Category', 'Status', 'Summary', 'Link']}
            rows={workHistory}
            renderRow={(item) => (
              <tr key={`${item.work_type}-${item.id}`}>
                <td><Link to={item.link_path} className="record-ref">{item.ref}</Link></td>
                <td>{formatInstantDate(item.date_raised)}</td>
                <td>{getWorkTypeLabel(item.work_type)}</td>
                <td>{displayValue(item.fault_category)}</td>
                <td><span className={`status-chip ${item.status}`}>{displayValue(item.status)}</span></td>
                <td>{item.summary || 'No summary recorded'}</td>
                <td><Link to={item.link_path} className="record-ref">Open</Link></td>
              </tr>
            )}
          />
        )}

        {activeTab === 'robot-checks' && (
          <div className="asset-checks-section">
            {/* Robot Checks (Brush/Spray) Section */}
            {robotChecks.length > 0 && (
              <div className="robot-checks-section">
                <div className="list-header">
                  <h3>Robot Checks</h3>
                </div>
                <div className="records-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Check Type</th>
                        <th>Submitted By</th>
                        <th>Ticket</th>
                        <th>Status</th>
                        <th>Photos</th>
                        <th>Issues</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {robotChecks.map((check) => {
                        const isCompleted = check.check_data?.check_completed === true;
                        const checkTypeLabel = check.form_type === 'brush_robot_check' ? 'Brush Robot Check' : 'Spray Robot Check';
                        const hasIssues = check.check_data?.cell_guarding_secure === 'false' ||
                          check.check_data?.cell_issues ||
                          check.check_data?.other_issues;
                        const issueSummary = hasIssues
                          ? (check.check_data?.cell_issues || check.check_data?.other_issues || 'Cell guarding issue').substring(0, 50)
                          : null;

                        return (
                          <tr key={check.check_id}>
                            <td>
                              {new Date(check.submitted_at).toLocaleString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td>{checkTypeLabel}</td>
                            <td>{check.submitted_by_name || 'Unknown'}</td>
                            <td>
                              {check.ticket_ref ? (
                                <Link to={`/tickets/${check.ticket_id}`} className="record-ref">{check.ticket_ref}</Link>
                              ) : '-'}
                            </td>
                            <td>
                              <span className={`status-chip ${isCompleted ? 'completed' : 'in_progress'}`}>
                                {isCompleted ? 'Completed' : 'In Progress'}
                              </span>
                            </td>
                            <td className="photos-cell">
                              {check.attachments?.length > 0 ? check.attachments.length : '-'}
                            </td>
                            <td className={hasIssues ? 'issue-cell' : ''}>
                              {issueSummary ? `${issueSummary}${issueSummary.length >= 50 ? '...' : ''}` : '-'}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn-text"
                                onClick={() => setSelectedRobotCheck(check)}
                              >
                                Review Check
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Generic Template Checks Section */}
            {genericTemplateChecksError && (
              <div className="generic-checks-warning">
                <span className="warning-icon">⚠</span>
                <span>{genericTemplateChecksError}</span>
              </div>
            )}

            {genericTemplateChecks.length > 0 && (
              <div className="generic-checks-section">
                <div className="list-header">
                  <h3>Template-Based Checks</h3>
                </div>
                <div className="records-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Check Type</th>
                        <th>Work Log Type</th>
                        <th>Submitted By</th>
                        <th>Ticket</th>
                        <th>Status</th>
                        <th>Photos</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {genericTemplateChecks.map((check) => {
                        const isCompleted = check.check_completed === true;
                        const checkName = check.template_snapshot?.name || check.form_type || 'Unknown Check';
                        const workLogTypeLabel = {
                          site_work: 'Site Work',
                          remote_session: 'Remote Session',
                          diagnosis: 'Diagnosis',
                          phone_call: 'Phone Call',
                          whatsapp: 'WhatsApp',
                          handover_note: 'Handover Note',
                          parts_fit: 'Parts Fit',
                          customer_update: 'Customer Update'
                        }[check.work_log_type] || check.work_log_type || '-';

                        return (
                          <tr key={check.check_id}>
                            <td>
                              {check.submitted_at ? new Date(check.submitted_at).toLocaleString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-'}
                            </td>
                            <td>{checkName}</td>
                            <td>{workLogTypeLabel}</td>
                            <td>{check.submitted_by_name || 'Unknown'}</td>
                            <td>
                              {check.ticket_ref ? (
                                <Link to={`/tickets/${check.ticket_id}`} className="record-ref">{check.ticket_ref}</Link>
                              ) : '-'}
                            </td>
                            <td>
                              <span className={`status-chip ${isCompleted ? 'completed' : 'in_progress'}`}>
                                {isCompleted ? 'Completed' : 'In Progress'}
                              </span>
                            </td>
                            <td className="photos-cell">
                              {check.attachments?.length > 0 ? check.attachments.length : '-'}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn-text"
                                onClick={() => setSelectedGenericCheck(check)}
                              >
                                Review Check
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty state when no checks of any type */}
            {robotChecks.length === 0 && genericTemplateChecks.length === 0 && !genericTemplateChecksError && (
              <div className="empty-message">No asset checks recorded.</div>
            )}
          </div>
        )}

        {activeTab === 'tickets' && (
          <RecordTable
            title="Support Tickets"
            emptyText="No tickets for this asset"
            columns={['Ref', 'Subject', 'Status', 'Priority', 'Opened', 'Resolved']}
            rows={tickets}
            renderRow={(ticket) => (
              <tr key={ticket.ticket_id}>
                <td><Link to={`/tickets/${ticket.ticket_id}`} className="record-ref">{ticket.ticket_ref}</Link></td>
                <td>
                  {ticket.subject}
                  {ticket.previous_ticket_ref && (
                    <span className="followon-history-tag">Follow-on of {ticket.previous_ticket_ref}</span>
                  )}
                </td>
                <td><span className={`status-chip ${ticket.status}`}>{displayValue(ticket.status)}</span></td>
                <td>{displayValue(ticket.priority)}</td>
                <td>{formatInstantDate(ticket.opened_at)}</td>
                <td>{formatInstantDate(ticket.resolved_at)}</td>
              </tr>
            )}
          />
        )}

        {activeTab === 'tasks' && (
          <RecordTable
            title="Scheduled Tasks"
            emptyText="No scheduled tasks for this asset"
            columns={['Ref', 'Title', 'Type', 'Status', 'Scheduled', 'Completed']}
            rows={tasks}
            renderRow={(task) => (
              <tr key={task.task_id}>
                <td><Link to={`/tasks/${task.task_id}`} className="record-ref">{task.task_ref}</Link></td>
                <td>
                  {task.title}
                  {Number(task.asset_count) > 1 && (
                    <div className="stat-sub" title={task.asset_refs || undefined}>one of {Number(task.asset_count)} robots on this job</div>
                  )}
                </td>
                <td>{displayValue(task.task_type)}</td>
                <td><span className={`status-chip ${task.status}`}>{displayValue(task.status)}</span></td>
                <td>{formatDate(task.scheduled_date)}</td>
                <td>{formatInstantDate(task.completed_at)}</td>
              </tr>
            )}
          />
        )}

        {activeTab === 'audit' && (
          <div className="audit-log-section">
            <h3>Audit Log</h3>
            {auditLog.length === 0 ? (
              <div className="empty-state">No changes have been recorded for this asset.</div>
            ) : (
              <div className="audit-timeline">
                {(() => {
                  const groupedByTimestamp = auditLog.reduce((acc, entry) => {
                    const key = `${entry.changed_at}-${entry.changed_by_name}`;
                    if (!acc[key]) {
                      acc[key] = {
                        changed_at: entry.changed_at,
                        changed_by_name: entry.changed_by_name,
                        change_type: entry.change_type,
                        changes: []
                      };
                    }
                    acc[key].changes.push(entry);
                    return acc;
                  }, {});

                  return Object.values(groupedByTimestamp).map((group, idx) => (
                    <div key={idx} className="audit-entry">
                      <div className="audit-header">
                        <span className="audit-user">{group.changed_by_name}</span>
                        <span className={`audit-type ${group.change_type}`}>{group.change_type}</span>
                        <span className="audit-date">
                          {new Date(group.changed_at).toLocaleString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="audit-changes">
                        {group.changes.map((change, cIdx) => (
                          <div key={cIdx} className="audit-change-row">
                            <span className="audit-field">{FIELD_LABELS[change.field_name] || change.field_name}</span>
                            <span className="audit-old">{change.old_value || '(empty)'}</span>
                            <span className="audit-arrow">→</span>
                            <span className="audit-new">{change.new_value || '(empty)'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Configure Service Schedule Modal (non-robot assets) */}
      {showCadenceModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Configure Service Schedule</h2>
              <button className="modal-close" onClick={() => setShowCadenceModal(false)}>&times;</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setCadenceSaving(true);
              setCadenceError(null);
              try {
                const response = await authFetch(`/api/v1/maintenance/assets/${id}/cadence`, {
                  method: 'POST',
                  body: JSON.stringify({
                    service_name: cadenceForm.service_name,
                    service_type: cadenceForm.service_type,
                    cadence_interval: parseInt(cadenceForm.cadence_interval, 10),
                    cadence_unit: cadenceForm.cadence_unit,
                    first_due_date: cadenceForm.first_due_date,
                    end_date: cadenceForm.end_date || null,
                    occurrences: cadenceForm.occurrences ? parseInt(cadenceForm.occurrences, 10) : null,
                    notes: cadenceForm.notes || null
                  })
                });
                if (!response.ok) {
                  const data = await response.json();
                  throw new Error(data.message || 'Failed to configure service schedule');
                }
                setShowCadenceModal(false);
                fetchMaintenanceTasks();
                fetchNextMaintenance();
              } catch (err) {
                setCadenceError(err.message);
              } finally {
                setCadenceSaving(false);
              }
            }}>
              <div className="modal-body">
                {cadenceError && <div className="modal-error">{cadenceError}</div>}
                <p className="modal-info">
                  Reconfiguring a visit type replaces its future scheduled visits. A servicing
                  inspection and an annual inspection schedule can run side by side.
                </p>
                <div className="form-group">
                  <label>Service Name *</label>
                  <input
                    type="text"
                    value={cadenceForm.service_name}
                    onChange={e => setCadenceForm(prev => ({ ...prev, service_name: e.target.value }))}
                    placeholder="e.g. Quarterly Servicing Inspection"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Visit Type *</label>
                  <select
                    value={cadenceForm.service_type}
                    onChange={e => setCadenceForm(prev => ({ ...prev, service_type: e.target.value }))}
                  >
                    <option value="health_check">Servicing Inspection</option>
                    <option value="annual_service">Annual Inspection</option>
                    <option value="custom_service">Custom Service</option>
                  </select>
                  <span className="field-hint">Determines which check form is presented when the visit is signed off.</span>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Repeat Every *</label>
                    <input
                      type="number"
                      min="1"
                      value={cadenceForm.cadence_interval}
                      onChange={e => setCadenceForm(prev => ({ ...prev, cadence_interval: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit *</label>
                    <select
                      value={cadenceForm.cadence_unit}
                      onChange={e => setCadenceForm(prev => ({ ...prev, cadence_unit: e.target.value }))}
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Due Date *</label>
                    <input
                      type="date"
                      value={cadenceForm.first_due_date}
                      onChange={e => setCadenceForm(prev => ({ ...prev, first_due_date: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={cadenceForm.end_date}
                      onChange={e => setCadenceForm(prev => ({ ...prev, end_date: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Max Occurrences</label>
                  <input
                    type="number"
                    min="1"
                    value={cadenceForm.occurrences}
                    onChange={e => setCadenceForm(prev => ({ ...prev, occurrences: e.target.value }))}
                    placeholder="Leave blank for 20 (default)"
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    rows={2}
                    value={cadenceForm.notes}
                    onChange={e => setCadenceForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowCadenceModal(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={cadenceSaving}>
                  {cadenceSaving ? 'Saving...' : 'Create Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Completed Maintenance Task Detail Modal */}
      {selectedMaintenanceTask && (
        <div className="modal-overlay" onClick={() => setSelectedMaintenanceTask(null)}>
          <div className="modal maintenance-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Completed Maintenance Details</h2>
              <button className="modal-close" onClick={() => setSelectedMaintenanceTask(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Task Reference</label>
                  <span className="record-ref">{selectedMaintenanceTask.task_ref}</span>
                </div>
                <div className="detail-item">
                  <label>Task Type</label>
                  <span>
                    {selectedMaintenanceTask.task_type === 'annual_service'
                      ? 'Annual Service'
                      : `Quarterly Inspection ${selectedMaintenanceTask.health_check_number}`}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Completed Date</label>
                  <span>{formatDate(selectedMaintenanceTask.completed_date)}</span>
                </div>
                <div className="detail-item">
                  <label>Completed By</label>
                  <span>{selectedMaintenanceTask.completed_by_name || 'Unknown'}</span>
                </div>
                {selectedMaintenanceTask.days_overdue > 0 && (
                  <div className="detail-item">
                    <label>Days Overdue</label>
                    <span className="overdue-text">{selectedMaintenanceTask.days_overdue} days late</span>
                  </div>
                )}
                {selectedMaintenanceTask.completion_outcome && (
                  <div className="detail-item full-width">
                    <label>Outcome</label>
                    <span>{selectedMaintenanceTask.completion_outcome}</span>
                  </div>
                )}
                {selectedMaintenanceTask.notes && (
                  <div className="detail-item full-width">
                    <label>Notes</label>
                    <p className="notes-text">{selectedMaintenanceTask.notes}</p>
                  </div>
                )}
              </div>

              {selectedMaintenanceTask.completion_attachments && selectedMaintenanceTask.completion_attachments.length > 0 && (
                <div className="attachments-section">
                  <h4>Sign-off Attachments</h4>
                  <div className="attachments-grid">
                    {selectedMaintenanceTask.completion_attachments.map((attachment, index) => (
                      <a
                        key={attachment.attachment_id || index}
                        href={authedFileHref(attachment.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="attachment-item"
                      >
                        {attachment.type && attachment.type.startsWith('image/') ? (
                          <img src={authedFileHref(attachment.url)} alt={attachment.name} className="attachment-thumbnail" />
                        ) : (
                          <div className="attachment-icon">
                            {attachment.type === 'application/pdf' ? 'PDF' : 'FILE'}
                          </div>
                        )}
                        <span className="attachment-name">{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selectedMaintenanceTask.follow_up_required && (
                <div className="follow-up-notice">
                  Follow-up required for this task
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Robot Check Detail Modal */}
      {selectedRobotCheck && (
        <RobotCheckModal
          check={selectedRobotCheck}
          onClose={() => setSelectedRobotCheck(null)}
          formatDate={formatDate}
        />
      )}

      {/* Generic Template Check Detail Modal */}
      {selectedGenericCheck && (
        <GenericTemplateCheckModal
          check={selectedGenericCheck}
          onClose={() => setSelectedGenericCheck(null)}
        />
      )}
    </div>
  );
}

const BRUSH_ROBOT_CHECK_LABELS = {
  cell_guarding_secure: 'Is robot cell guarding secure and damage free?',
  cell_issues: 'What are the issues with the robot cell?',
  air_regulator_ok: 'Check air regulator has sufficient air motor oil and pressure.',
  base_plate_secure: 'Check all base plate connections are secure and damage free.',
  tof_camera_secure: 'Check TOF camera and ensure connections are secure.',
  eoat_frame_ok: 'Is there any excessive damage to the EOAT frame?',
  bolts_secure: 'Check all tool and robot bolts are secure.',
  bearing_gearbox_ok: 'Inspect front bearing, gearbox and bushes.',
  eoat_functions_ok: 'Check operation of all EOAT functions work properly.',
  strut_ok: 'Check strut works correctly.',
  end_cap_secure: 'Is the end cap strong/secure enough to avoid injury to cows?',
  robot_bag_ok: 'Is the robot bag free of holes and relatively clean?',
  drench_ok: 'Does the drench operate correctly and clean the tool efficiently?',
  system_efficient: 'Does the system perform efficiently?',
  remote_access_ok: 'Can you access TeamViewer, TOFs, HMI and Virtual Pendant without issues?',
  cctv_ok: 'Are the CCTV cameras online with a clear and clean view?',
  shed_office_secure: 'Check shed/office is secure and dry.',
  pump_box_ok: 'Inspect Pump Box.',
  sufficient_spares: 'Does the farm have sufficient spares?',
  other_issues: 'Are there any other issues found with the system as a whole?',
  check_completed: 'Pre Robot Check Complete'
};

const SPRAY_ROBOT_CHECK_LABELS = {
  cell_guarding_secure: 'Is robot cell guarding secure and damage free?',
  cell_issues: 'What are the issues with the robot cell?',
  base_plate_secure: 'Check all base plate connections are secure and damage free.',
  tof_camera_secure: 'Check TOF camera/s connections are secure.',
  eoat_frame_ok: 'Is there any excessive damage to the EOAT frame?',
  bolts_secure: 'Check all tool bolts are secure.',
  eoat_functions_ok: 'Check operation of all EOAT functions work properly.',
  drench_ok: 'Is the drench operating correctly and cleaning tool efficiently?',
  end_cap_secure: 'Is the end cap strong/secure enough to avoid injury to cows?',
  robot_bag_ok: 'Is the robot bag free of holes and relatively clean?',
  system_efficient: 'Does the system perform efficiently?',
  remote_access_ok: 'Can you access TeamViewer, TOFs, HMI and Virtual Pendant without issues?',
  cctv_ok: 'Are the CCTV cameras online with a clear and clean view?',
  shed_office_secure: 'Check shed/office is secure and dry.',
  pump_box_ok: 'Inspect Pump Box.',
  sufficient_spares: 'Does the farm have sufficient spares?',
  other_issues: 'Are there any other issues found with the system as a whole?',
  check_completed: 'Post Robot Check Complete'
};

const BRUSH_CHECK_FIELD_ORDER = [
  'cell_guarding_secure', 'cell_issues', 'air_regulator_ok', 'base_plate_secure',
  'tof_camera_secure', 'eoat_frame_ok', 'bolts_secure', 'bearing_gearbox_ok',
  'eoat_functions_ok', 'strut_ok', 'end_cap_secure', 'robot_bag_ok', 'drench_ok',
  'system_efficient', 'remote_access_ok', 'cctv_ok', 'shed_office_secure',
  'pump_box_ok', 'sufficient_spares', 'other_issues', 'check_completed'
];

const SPRAY_CHECK_FIELD_ORDER = [
  'cell_guarding_secure', 'cell_issues', 'base_plate_secure', 'tof_camera_secure',
  'eoat_frame_ok', 'bolts_secure', 'eoat_functions_ok', 'drench_ok', 'end_cap_secure',
  'robot_bag_ok', 'system_efficient', 'remote_access_ok', 'cctv_ok',
  'shed_office_secure', 'pump_box_ok', 'sufficient_spares', 'other_issues', 'check_completed'
];

function RobotCheckModal({ check, onClose, formatDate }) {
  const isBrush = check.form_type === 'brush_robot_check';
  const checkData = check.check_data || {};
  const attachments = check.attachments || [];
  const isCompleted = checkData.check_completed === true;

  const isSpray = check.form_type === 'spray_robot_check';
  // Check if this is a template-based check (for Brush or Spray)
  const hasTemplateSnapshot = (isBrush || isSpray) && checkData.template_snapshot && checkData.template_answers;

  const getStepAttachments = (stepKey) => {
    return attachments.filter(att => att.step_key === stepKey);
  };

  const getPhotoUrl = (att) => {
    if (att.url?.startsWith('/uploads/')) return authedFileHref(att.url);
    return att.url || '';
  };

  // Template-based display: the shared read-only view (CheckFormReview), so a
  // robot check shows every question type the same way as everywhere else
  // (Workscope forms Phase 1, 2026-10-22).
  const renderTemplateBasedCheck = () => (
    <CheckFormReview
      check={{
        template_snapshot: checkData.template_snapshot,
        template_answers: checkData.template_answers,
        attachments,
        check_completed: isCompleted,
        template_version: checkData.template_version
      }}
    />
  );

  // Hard-coded fallback display
  const renderHardCodedCheck = () => {
    const labels = isBrush ? BRUSH_ROBOT_CHECK_LABELS : SPRAY_ROBOT_CHECK_LABELS;
    const fieldOrder = isBrush ? BRUSH_CHECK_FIELD_ORDER : SPRAY_CHECK_FIELD_ORDER;

    const formatValue = (field, value) => {
      if (field === 'check_completed') {
        return value === true ? 'Yes' : 'No';
      }
      if (field === 'cell_issues' || field === 'other_issues') {
        return value || 'None';
      }
      if (value === 'true') return 'Yes';
      if (value === 'false') return 'No';
      if (value === '' || value === null || value === undefined) return 'Not checked';
      return String(value);
    };

    const getValueClass = (field, value) => {
      if (field === 'check_completed') {
        return value === true ? 'value-pass' : 'value-fail';
      }
      if (field === 'cell_issues' || field === 'other_issues') {
        return value ? 'value-note' : '';
      }
      if (field === 'eoat_frame_ok') {
        return value === 'true' ? 'value-fail' : value === 'false' ? 'value-pass' : 'value-unchecked';
      }
      if (value === 'true') return 'value-pass';
      if (value === 'false') return 'value-fail';
      return 'value-unchecked';
    };

    return (
      <div className="robot-check-answers">
        {fieldOrder.map((field) => {
          const value = checkData[field];
          const label = labels[field];
          if (!label) return null;
          if (field === 'cell_issues' && checkData.cell_guarding_secure !== 'false') return null;

          const stepPhotos = getStepAttachments(field);
          const hasPhotos = stepPhotos.length > 0;

          return (
            <div key={field} className={`answer-row ${field === 'cell_issues' || field === 'other_issues' ? 'full-width' : ''} ${hasPhotos ? 'has-photos' : ''}`}>
              <div className="answer-content">
                <span className="answer-label">{label}</span>
                <span className={`answer-value ${getValueClass(field, value)}`}>
                  {formatValue(field, value)}
                </span>
              </div>
              {hasPhotos && (
                <div className="answer-photos">
                  {stepPhotos.map((att, idx) => (
                    <a
                      key={att.attachment_id || idx}
                      href={getPhotoUrl(att)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="answer-photo-thumb"
                    >
                      <img src={getPhotoUrl(att)} alt={att.name || 'Photo'} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal robot-check-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isBrush ? 'Brush Robot Check' : 'Spray Robot Check'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="robot-check-summary">
            <div className="summary-item">
              <label>Submitted</label>
              <span>
                {new Date(check.submitted_at).toLocaleString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            <div className="summary-item">
              <label>By</label>
              <span>{check.submitted_by_name || 'Unknown'}</span>
            </div>
            {check.ticket_ref && (
              <div className="summary-item">
                <label>Ticket</label>
                <span className="record-ref">{check.ticket_ref}</span>
              </div>
            )}
            <div className="summary-item">
              <label>Status</label>
              <span className={`status-chip ${isCompleted ? 'completed' : 'in_progress'}`}>
                {isCompleted ? 'Completed' : 'In Progress'}
              </span>
            </div>
          </div>

          {hasTemplateSnapshot ? renderTemplateBasedCheck() : renderHardCodedCheck()}
        </div>
      </div>
    </div>
  );
}

/**
 * GenericTemplateCheckModal
 * Displays a generic template check from the saved template_snapshot.
 * Unlike robot checks (Brush/Spray), this renders arbitrary template-based checks
 * for assets like BCI Parlour, ID Verify, etc.
 */
function GenericTemplateCheckModal({ check, onClose }) {
  const schema = check.template_snapshot;
  const isCompleted = check.check_completed === true;

  const checkName = schema?.name || check.form_type || 'Template Check';
  const workLogTypeLabel = {
    site_work: 'Site Work',
    remote_session: 'Remote Session',
    diagnosis: 'Diagnosis',
    phone_call: 'Phone Call',
    whatsapp: 'WhatsApp',
    handover_note: 'Handover Note',
    parts_fit: 'Parts Fit',
    customer_update: 'Customer Update'
  }[check.work_log_type] || check.work_log_type || 'Unknown';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal generic-check-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{checkName}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="robot-check-summary">
            <div className="summary-item">
              <label>Submitted</label>
              <span>
                {check.submitted_at ? new Date(check.submitted_at).toLocaleString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : '-'}
              </span>
            </div>
            <div className="summary-item">
              <label>By</label>
              <span>{check.submitted_by_name || 'Unknown'}</span>
            </div>
            <div className="summary-item">
              <label>Work Type</label>
              <span>{workLogTypeLabel}</span>
            </div>
            {check.ticket_ref && (
              <div className="summary-item">
                <label>Ticket</label>
                <span className="record-ref">{check.ticket_ref}</span>
              </div>
            )}
            <div className="summary-item">
              <label>Status</label>
              <span className={`status-chip ${isCompleted ? 'completed' : 'in_progress'}`}>
                {isCompleted ? 'Completed' : 'In Progress'}
              </span>
            </div>
          </div>

          {/* The answers and photos, as on the Timeline and Work Logs tabs. */}
          <CheckFormReview check={check} />
        </div>
      </div>
    </div>
  );
}

function RecordTable({ title, emptyText, columns, rows, renderRow }) {
  return (
    <div className="records-section">
      <div className="list-header">
        <h3>{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="empty-message">{emptyText}</div>
      ) : (
        <div className="records-table">
          <table>
            <thead>
              <tr>{columns.map(column => <th key={column}>{column}</th>)}</tr>
            </thead>
            <tbody>{rows.map(renderRow)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AssetDetail;
